import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/pages/home.css';
import Navbar from '../components/common/Navbar';
import { fetchSubscriptionPlans } from '../utils/api'; // Add this import

const Home = () => {
    const [activeSlide, setActiveSlide] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);
    const [country, setCountry] = useState(null);
    const [locationLoading, setLocationLoading] = useState(true);
    const [plans, setPlans] = useState([]);

    // Hard-coded Rand prices (same as payment modals)
    const randPrices = {
        1: 100, // Basic plan price in Rand
        2: 370  // Plus plan price in Rand
    };

    // Handle scroll for navbar transparency
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Auto slide carousel with better interval control
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveSlide((prev) => (prev === 2 ? 0 : prev + 1));
        }, 5000);

        // Clear interval on component unmount
        return () => clearInterval(interval);
    }, []);

    // Detect user's location for pricing
    useEffect(() => {
        const detectLocation = async () => {
            try {
                setLocationLoading(true);

                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();

                setCountry(data.country_code);
            } catch (error) {
                console.error('Error detecting location:', error);
                setCountry('UNKNOWN');
            } finally {
                setLocationLoading(false);
            }
        };

        detectLocation();
    }, []);

    // Load plans
    useEffect(() => {
        const loadPlans = async () => {
            try {
                const plansData = await fetchSubscriptionPlans();
                setPlans(plansData);
            } catch (error) {
                console.error('Error loading plans:', error);
            }
        };

        loadPlans();
    }, []);

    // Get price display based on location
    const getPriceDisplay = (plan) => {
        if (country === 'ZA') {
            return `R${randPrices[plan.id]?.toFixed(2) || '0.00'}`;
        } else {
            return `$${parseFloat(plan.Price).toFixed(2)}`;
        }
    };

    // Get currency info
    const getCurrencyInfo = () => {
        if (locationLoading) return { text: 'Loading...', symbol: '' };
        if (country === 'ZA') return { text: 'Pricing in South African Rand', symbol: 'ZAR' };
        return { text: 'Pricing in US Dollars', symbol: 'USD' };
    };

    const scrollToPlans = () => {
        document.getElementById('plans-section').scrollIntoView({
            behavior: 'smooth'
        });
    };

    // Carousel data for better maintainability
    const carouselSlides = [
        {
            id: 0,
            background: '/img/blockchain-background.jpg',
            title: 'Secure File Storage',
            description: 'Store and backup your important files with military-grade encryption'
        },
        {
            id: 1,
            background: '/img/bankcard-backround.jpg',
            title: 'Banking Information Security',
            description: 'Keep your banking details safe with triple-layer encryption technology'
        },
        {
            id: 2,
            background: '/img/blockchain-background.jpg',
            title: 'Crypto Wallet Phrase Storage',
            description: 'Never lose your blockchain keys or recovery phrases again!'
        }
    ];

    // Features data for easier maintenance
    const features = [
        {
            icon: 'fas fa-shield-alt',
            title: 'Secure Storage',
            description: 'Military-grade encryption for all your files and sensitive information'
        },
        {
            icon: 'fas fa-child',
            title: 'Endowment Center',
            description: 'Pass on digital assets to your loved ones securely and reliably'
        },
        {
            icon: 'fas fa-server',
            title: 'Decentralized Nodes',
            description: 'Enhanced security with globally distributed architecture'
        },
        {
            icon: 'fas fa-lock',
            title: 'Triple Encryption',
            description: 'Three-layer encryption protocol for maximum data protection'
        },
        {
            icon: 'fas fa-wallet',
            title: 'Cost-Effective',
            description: 'Premium security features at affordable subscription plans'
        },
        {
            icon: 'fas fa-key',
            title: 'Password Manager',
            description: 'Store and manage all your passwords in one secure location'
        }
    ];

    return (
        <div className="home-container">
            {/* Navbar */}
            <Navbar isScrolled={isScrolled} />

            {/* Hero Section with improved carousel */}
            <section className="hero-section">
                <div className="hero-carousel">
                    {carouselSlides.map((slide) => (
                        <div
                            key={slide.id}
                            className={`carousel-slide ${activeSlide === slide.id ? 'active' : ''}`}
                            style={{ backgroundImage: `url(${slide.background})` }}
                        >
                            <div className="overlay"></div>
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-8 col-md-10 mx-auto text-center">
                                        <h1 className="slide-title">{slide.title}</h1>
                                        <p className="slide-description">{slide.description}</p>

                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="carousel-indicators">
                    {carouselSlides.map((slide) => (
                        <button
                            key={slide.id}
                            className={activeSlide === slide.id ? 'active' : ''}
                            onClick={() => setActiveSlide(slide.id)}
                            aria-label={`Slide ${slide.id + 1}`}
                        ></button>
                    ))}
                </div>
                <div className="hero-buttons-static">
                    <Link to="/register" className="btn btn-primary me-3">Get Started</Link>
                    <button onClick={scrollToPlans} className="btn btn-outline me-3">View Plans</button>
                    <Link to="/login" className="btn btn-outline">Log In</Link>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section">
                <div className="container">
                    <div className="row features-row justify-content-center">
                        {features.map((feature, index) => (
                            <div key={index} className="col-lg-3 col-md-6 col-sm-12">
                                <div className="feature-card">
                                    <div className="feature-icon">
                                        <i className={feature.icon}></i>
                                    </div>
                                    <h3>{feature.title}</h3>
                                    <p>{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Plans Section */}
            <section className="plans-section" id="plans-section">
                <div className="container">
                    {locationLoading ? (
                        <div className="row">
                            <div className="col-12 text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                                <p className="mt-2">Loading pricing for your location...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="row plans-row justify-content-center">
                            {plans.map((plan) => (
                                <div key={plan.id} className="col-lg-3 col-md-6 col-sm-12">
                                    <div className={`plan-card ${plan.id === 2 ? 'featured-plan' : ''}`}>
                                        {plan.id === 2 && <div className="popular-badge">Most Popular</div>}
                                        <div className="plan-icon">
                                            <i className={plan.id === 1 ? 'fas fa-user' : plan.id === 2 ? 'fas fa-users' : 'fas fa-building'}></i>
                                        </div>
                                        <h3 className='text-white'>{plan.Name}</h3>
                                        <div className="plan-price">
                                            <span className="price-amount">{getPriceDisplay(plan)}</span>
                                            <span className="price-period text-white">
                                                {plan.id === 3 ? '2 weeks' : 'year'}
                                            </span>
                                        </div>
                                        <div className="plan-features text-white">
                                            <p className='text-white'>✓ {plan.Feature1}</p>
                                            <p className='text-white'>✓ {plan.Feature2}</p>
                                            <p className='text-white'>✓ {plan.Feature3}</p>
                                        </div>
                                        <Link
                                            to="/register"
                                            className={`btn ${plan.id === 2 ? 'btn-primary' : 'btn-outline'} plan-btn`}
                                        >
                                            {plan.Name === "Enterprise" ? "Contact Sales" : "Get Started"}
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto text-center">
                            <h2>Ready to secure your digital assets?</h2>
                            <p>Experience the peace of mind that comes with knowing your digital information is safe and secure.</p>
                            <Link to="/register" className="btn btn-primary btn-lg">Get Started Today</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-bottom">
                    <div className="row">
                        <div className="col-md-12 text-center">
                            <p>&copy; {new Date().getFullYear()} PhēnixLoc. All rights reserved.</p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Home;